import { useCallback, useEffect } from 'react';
import LoadBar from '@/components/LoadBar/LoadBar';
import SelectFloatingLabel, {
  SelectOptionData
} from '@/components/SelectFloatingLabel/SelectFloatingLabel';
import { useCollection } from '@/firebase/hooks/useCollection';
import { PurchaseOrder, PurchaseOrderAssignQcForm } from '@/types/PurchaseOrder';
import { Timestamp, doc, updateDoc, where } from 'firebase/firestore';
import * as yup from 'yup';
import { Shape } from '@/types/Forms';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DatePicker from '@/components/DatePicker/DatePicker';
import { useNotification } from '@/components/Notifications/NotificationProvider';
import firestore from '@/firebase/firestore';

type Props = {
  po: PurchaseOrder;
  onAssigned: () => Promise<void> | void;
  onClose: () => void;
};

const schema = yup.object<Shape<PurchaseOrderAssignQcForm>>().shape({
  qcUserId: yup.string().required('QC is mandatory'),
  reportId: yup.string().required('Checklist template is mandatory'),
  inspectionDate: yup.date().min(new Date()).required('Proposed date is mandatory')
});

function PoAssignModal({ po, onAssigned, onClose }: Props) {
  const formatUserName = useCallback((firstName?: string | null, lastName?: string | null) => {
    return [firstName, lastName].filter(Boolean).join(' ');
  }, []);

  // TODO: Should we filter also for client and factory?
  const { loading: usersLoading, data: users } = useCollection(
    'users',
    false,
    where('type', '==', 'QC')
  );

  const { loading: reportsLoading, data: reports } = useCollection(
    'reports',
    false,
    where('clients', 'array-contains', po.clientId)
  );

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
    handleSubmit
  } = useForm<PurchaseOrderAssignQcForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      qcUserId: '',
      reportId: '',
      inspectionDate: new Date()
    }
  });

  useEffect(() => {
    const getDateValue = (value?: Date | Timestamp | null) => {
      if (!value) return new Date();
      if (value instanceof Timestamp) return value.toDate();
      return value;
    };

    reset({
      qcUserId: po.qcUserId || '',
      reportId: po.reportId || '',
      inspectionDate: getDateValue(po.inspectionDate || po.proposedDate)
    });
  }, [po.id, po.inspectionDate, po.proposedDate, po.qcUserId, po.reportId, reset]);

  const { showNotification } = useNotification();

  const onSubmit = useCallback(
    async (data: PurchaseOrderAssignQcForm) => {
      await updateDoc(doc(firestore, 'orders/' + po.id || ''), {
        qcUserId: data.qcUserId,
        reportId: data.reportId,
        inspectionDate: data.inspectionDate,
        updatedAt: Timestamp.now()
      });
      showNotification({
        type: 'success',
        text: 'QC assigned successfully'
      });
      onClose();
      await onAssigned();
    },
    [onAssigned, onClose, po.id, showNotification]
  );

  if (!usersLoading && !reportsLoading && (!users?.length || !reports?.length)) {
    return (
      <div
        className="fixed inset-0 z-50 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true">
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"></div>
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <div
            className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="w-full sm:flex sm:items-start">
                <div className="mt-3 w-full text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-headline">
                    Assign QC user to PO #{po.number}
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Please create a QC user and a checklist template before assigning QC to this
                      PO.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="button"
                className="inline-flex w-full justify-center rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const selectedUser = users.find((u) => u.id === watch('qcUserId'));
  const selectedReport = reports.find((r) => r.id === watch('reportId'));

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"></div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
          &#8203;
        </span>
        <div
          className="inline-block transform rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="w-full sm:flex sm:items-start">
              <div className="mt-3 w-full text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg font-medium leading-6 text-gray-900" id="modal-headline">
                  Assign QC user to PO #{po.number}
                </h3>
                {usersLoading || reportsLoading ? (
                  <LoadBar />
                ) : (
                  <>
                    <div className="mt-2">
                      <Controller
                        control={control}
                        name="qcUserId"
                        render={({ field }) => (
                          <SelectFloatingLabel<
                            PurchaseOrderAssignQcForm,
                            'qcUserId',
                            SelectOptionData
                          >
                            items={users
                              ?.filter((u) => !u.deletedAt)
                              ?.map((f) => ({
                                label: formatUserName(f.firstName, f.lastName),
                                value: f.id
                              }))}
                            field={{
                              ...field,
                              onChange: (e: SelectOptionData) => {
                                field.onChange(e.value);
                              },
                              value: selectedUser
                                ? ({
                                    label: formatUserName(
                                      selectedUser?.firstName,
                                      selectedUser?.lastName
                                    ),
                                    value: field.value
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  } as any)
                                : undefined
                            }}
                            required={true}
                            label="Select a user"
                            errors={errors}
                          />
                        )}
                      />
                    </div>
                    <div className="mt-2">
                      <Controller
                        control={control}
                        name="reportId"
                        render={({ field }) => (
                          <SelectFloatingLabel<
                            PurchaseOrderAssignQcForm,
                            'reportId',
                            SelectOptionData
                          >
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            items={reports?.map((f: any) => ({
                              label: f.title,
                              value: f.id
                            }))}
                            field={{
                              ...field,
                              onChange: (e: SelectOptionData) => {
                                field.onChange(e.value);
                              },
                              value: selectedReport
                                ? ({
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    label: (selectedReport as any)?.title,
                                    value: field.value
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  } as any)
                                : undefined
                            }}
                            required={true}
                            label="Select a checklist template"
                            errors={errors}
                          />
                        )}
                      />
                    </div>
                    <div className="mt-2">
                      <DatePicker
                        date={watch('inspectionDate') || new Date()}
                        label="Inspection date"
                        placeholder="Select an inspection date"
                        minDate={new Date()}
                        required
                        onChange={(d) => {
                          setValue('inspectionDate', d);
                        }}
                      />
                      {errors.inspectionDate && (
                        <span className="text-sm text-red-500">
                          {errors.inspectionDate.message || 'This field is required'}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={handleSubmit(onSubmit)}>
              Assign
            </button>
            <button
              type="button"
              className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoAssignModal;
